import React, { useState, useEffect, useRef } from "react";
import { proxyAvatar } from "../../helper/ImageProxy";
import {
  ActionIcon,
  Menu,
  Badge,
  Text,
  ScrollArea,
  Flex,
  Paper,
  Divider,
  Button,
  Box,
  Group,
  Indicator,
  Transition,
  ThemeIcon,
  Tabs,
} from "@mantine/core";
import {
  IconBell,
  IconFileText,
  IconClock,
  IconWritingSign,
  IconMessage,
  IconChevronRight,
  IconCheck,
  IconDots,
} from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { useDocumentTitle } from "@mantine/hooks";
import * as signalR from "@microsoft/signalr";
import { repositorySign } from "../../_const/_constVar";
import { BASE_API_SIGN, BASE_API_SIGN_LOCAL } from "../../_const";
import { MessageResponse } from "../../../model/MessageResponse";
import { formatDateTransfer } from "../../extension/FormatDate";
import { DocumentStepInfoModel } from "../../../model/DocumentStepInfoModel";
import { NotificationExtension } from "../../extension/NotificationExtension";
import { UserModel } from "../../../model/UserModel";
import { Avatar } from "@mantine/core";
import Avvvatars from "avvvatars-react";

interface ListAccessSignSearchModel {
  skip: number;
  take: number;
  keySearch?: string;
  fromDate?: Date | null;
  toDate?: Date | null;
  createBy?: string;
  sort?: string;
  isOrder?: boolean;
}

interface Notificationuser {
  id: string;
  userid: number;
  content: string;
  link: string;
  createDate: string;
  read: boolean;
  createBy: string;
  type: number;
  creatorName?: string;
  creatorAvatar?: string;
  documentName?: string;
}

// Sign types mapping updated to match your system
const signTypes = [
  { value: 0, label: "Duyệt", color: "teal" },
  { value: 1, label: "Ký ảnh", color: "blue" },
  { value: 2, label: "Ký số", color: "cyan" },
  { value: 3, label: "Ký nháy", color: "violet" },
];

const getSignTypeInfo = (value: number) => {
  return signTypes.find((x) => x.value === value) || { value, label: "Khác", color: "gray" };
};

export const NotificationBell: React.FC = () => {
  const [pendingCount, setPendingCount] = useState(0);
  const [pendingDocuments, setPendingDocuments] = useState<
    DocumentStepInfoModel[]
  >([]);
  const [notifications, setNotifications] = useState<Notificationuser[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [menuOpened, setMenuOpened] = useState(false);
  const [title, setTitle] = useState('DocSign - Hệ thống ký số');
  const [isBlinking, setIsBlinking] = useState(false);
  const blinkIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const [notificationPage, setNotificationPage] = useState<{
    skip: number;
    take: number;
    hasMore: boolean;
    totalCount?: number;
  }>({ skip: 0, take: 20, hasMore: false }); // Start with false, will be set after first load
  const navigate = useNavigate();

  useDocumentTitle(title);

  const stopBlinking = () => {
    if (blinkIntervalRef.current) {
      clearInterval(blinkIntervalRef.current);
      blinkIntervalRef.current = null;
    }
    setIsBlinking(false);
    const unreadNotifications = notifications.filter((n) => !n.read);
    const totalUnreadCount = pendingCount + unreadNotifications.length;
    if (totalUnreadCount > 0) {
      setTitle(`(${totalUnreadCount}) DocSign - Hệ thống ký số`);
    } else {
      setTitle('DocSign - Hệ thống ký số');
    }
  };

  const startBlinking = (unreadCount: number) => {
    // Clear existing interval if any
    if (blinkIntervalRef.current) {
      clearInterval(blinkIntervalRef.current);
    }

    setIsBlinking(true);

    // Nhấp nháy nhanh hơn: 1.5 giây thay vì 15 giây
    blinkIntervalRef.current = setInterval(() => {
      setTitle(prevTitle => {
        if (prevTitle.includes('🔔')) {
          return `(${unreadCount}) DocSign - Hệ thống ký số`;
        } else {
          return `🔔 (${unreadCount}) Thông báo mới - DocSign`;
        }
      });
    }, 1500);

    // Stop blinking when user focuses on tab
    const focusHandler = () => {
      stopBlinking();
      document.removeEventListener('visibilitychange', visibilityHandler);
      window.removeEventListener('focus', focusHandler);
    };

    const visibilityHandler = () => {
      if (!document.hidden) {
        stopBlinking();
        document.removeEventListener('visibilitychange', visibilityHandler);
        window.removeEventListener('focus', focusHandler);
      }
    };

    window.addEventListener('focus', focusHandler);
    document.addEventListener('visibilitychange', visibilityHandler);

    // Auto stop after 2 minutes
    setTimeout(stopBlinking, 120000);
  };

  const updateDocumentTitle = (unreadCount: number, forceBlink: boolean = false) => {
    if (unreadCount > 0) {
      if (!isBlinking || forceBlink) {
        // Start blinking if not already blinking, or force start when new notification arrives
        startBlinking(unreadCount);
      } else {
        // Update count while already blinking
        setTitle(prevTitle => {
          if (prevTitle.includes('🔔')) {
            return `🔔 (${unreadCount}) Thông báo mới - DocSign`;
          } else {
            return `(${unreadCount}) DocSign - Hệ thống ký số`;
          }
        });
      }
    } else {
      stopBlinking();
      setTitle('DocSign - Hệ thống ký số');
    }
  };

  const fetchPendingDocuments = async () => {
    try {
      setIsLoading(true);
      const queryParams: ListAccessSignSearchModel = {
        skip: 0,
        take: 25,
        keySearch: "",
        sort: "",
        isOrder: false,
      };

      const baseUrl = "/api/v1/SignPage/list-access-sign";
      const queryString = Object.entries(queryParams)
        .reduce((acc, [key, value]) => {
          if (value !== undefined && value !== null && value !== "") {
            const stringValue =
              value instanceof Date ? value.toISOString() : value.toString();
            return `${acc}&${key}=${encodeURIComponent(stringValue)}`;
          }
          return acc;
        }, "")
        .replace(/^&/, "");
      const url = `${baseUrl}${queryString ? `?${queryString}` : ""}`;

      const response = await repositorySign.get<
        MessageResponse<DocumentStepInfoModel[]>
      >(url, true);

      if (response && response.success) {
        const dataList = response?.data || [];
        const prevCount = pendingCount;
        const newCount = dataList.length;

        setPendingDocuments(dataList);
        setPendingCount(newCount);

        if (newCount > prevCount && prevCount > 0) {
          const newDocumentsCount = newCount - prevCount;
          console.log(`${newDocumentsCount} hồ sơ mới cần ký`);
        }
      }
    } catch (error) {
      console.error("Error fetching pending documents:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchNotifications = async (triggerBlink: boolean = false, loadMore: boolean = false) => {
    try {
      const skip = loadMore ? notificationPage.skip : 0;
      const take = notificationPage.take;

      // Call API with pagination params
      const response = await repositorySign.get<
        MessageResponse<Notificationuser[]>
      >(`/api/v1/SignPage/get-noti-user?skip=${skip}&take=${take}`, true);

      if (response && response.success) {
        const newNotifications = response.data || [];
        const totalCount = (response as any).totalCount || newNotifications.length;
        const prevUnreadCount = notifications.filter((n) => !n.read).length;

        // Use totalCount from API to determine if there's more data
        if (loadMore) {
          // Append new data when loading more
          setNotifications((prev) => [...prev, ...newNotifications]);
          // Update pagination state based on totalCount
          const currentLoaded = notificationPage.skip + newNotifications.length;
          const hasMore = currentLoaded < totalCount;
          setNotificationPage((prev) => ({
            ...prev,
            skip: currentLoaded,
            totalCount: totalCount,
            hasMore: hasMore,
          }));
        } else {
          // Initial load - replace all data
          setNotifications(newNotifications);
          // Update pagination state based on totalCount
          const currentLoaded = newNotifications.length;
          const hasMore = currentLoaded < totalCount;
          setNotificationPage({
            skip: currentLoaded,
            take: take,
            totalCount: totalCount,
            hasMore: hasMore,
          });
        }

        // Calculate unread count for display
        const currentDisplayed = loadMore
          ? [...notifications, ...newNotifications]
          : (!loadMore && newNotifications.length > take
            ? newNotifications.slice(0, take)
            : newNotifications);
        const newUnreadCount = currentDisplayed.filter((n) => !n.read).length;

        // Log new notifications count (only for initial load/refresh, not load more)
        if (newUnreadCount > prevUnreadCount && prevUnreadCount > 0 && !loadMore) {
          const newNotificationsCount = newUnreadCount - prevUnreadCount;
          console.log(`${newNotificationsCount} thông báo comment mới`);
        }

        // If triggered by SignalR notification, force blink after state update
        if (triggerBlink && newUnreadCount > 0 && !loadMore) {
          setTimeout(() => {
            const totalUnreadCount = pendingCount + newUnreadCount;
            updateDocumentTitle(totalUnreadCount, true);
          }, 100);
        }
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  };

  const loadMoreNotifications = async () => {
    if (isLoadingMore || !notificationPage.hasMore) return;

    setIsLoadingMore(true);
    try {
      // Use server-side pagination with totalCount
      await fetchNotifications(false, true);
    } finally {
      setIsLoadingMore(false);
    }
  };

  const handleNotificationClick = async (notification: Notificationuser) => {
    try {
      await repositorySign.get(
        `/api/v1/SignPage/active-noti-user?id=${notification.id}`
      );
      // Remove from displayed notifications
      setNotifications((prev) => prev.filter((n) => n.id !== notification.id));
      // Update totalCount if exists
      if (notificationPage.totalCount) {
        setNotificationPage((prev) => ({
          ...prev,
          totalCount: (prev.totalCount || 0) - 1,
        }));
      }
      setMenuOpened(false);
      window.location.href = "/method/view-page/" + notification.link;
    } catch (error) {
      console.error("Error marking notification as read:", error);
      NotificationExtension.Fails("Lỗi khi cập nhật thông báo!");
    }
  };

  const handleDocumentClick = (document: DocumentStepInfoModel) => {
    setMenuOpened(false);
    const documentStepId = document.listStepId || document.documentId;
    if (documentStepId && documentStepId > 0) {
      window.open(`/method/sign-page/${documentStepId}`, "_blank");
    } else {
      NotificationExtension.Fails("Mã hồ sơ không hợp lệ!");
    }
  };

  const handleViewAll = () => {
    setMenuOpened(false);
    navigate("/method/approve-task");
  };

  useEffect(() => {
    fetchPendingDocuments();
    fetchNotifications();

    let isMounted = true;
    let connection: signalR.HubConnection | null = null;

    // Set up SignalR connection
    const userLoginStr = localStorage.getItem("userLogin");
    if (userLoginStr) {
      try {
        const userLogin: UserModel = JSON.parse(userLoginStr);
        const userId = userLogin.id;

        if (userId) {
          // Create SignalR connection
          connection = new signalR.HubConnectionBuilder()
            .withUrl(`https://socket-sign.hacom.vn/signalr`, {
              accessTokenFactory: () => {
                const token = localStorage.getItem("token");
                return token || "";
              },
              skipNegotiation: true,
              transport: signalR.HttpTransportType.WebSockets,
            })
            .withAutomaticReconnect()
            .configureLogging(signalR.LogLevel.Information)
            .build();

          // Start connection
          connection
            .start()
            .then(() => {
              if (isMounted) {
                console.log("SignalR Connected");
              }
              // Server automatically adds user to group in OnConnectedAsync based on user ID
              // No need to manually call JoinGroup
            })
            .catch((err) => {
              if (!isMounted || err?.name === "AbortError" || err?.message?.includes("stop() was called")) {
                console.log("SignalR connection aborted due to component unmount");
              } else {
                console.error("SignalR Connection Error: ", err);
              }
            });

          // Listen for notifications
          connection.on("ReceiveNotification", (data: any) => {
            console.log("Received notification:", data);
            // Call API to fetch latest notifications when receiving realtime notification
            // Pass triggerBlink=true to force title blinking for new notification
            fetchNotifications(true);
          });

          // Handle reconnection
          connection.onreconnecting(() => {
            console.log("SignalR Reconnecting...");
          });

          connection.onreconnected(() => {
            console.log("SignalR Reconnected");
            // Server automatically re-adds user to group on reconnection
            // No need to manually call JoinGroup
          });

          connection.onclose(() => {
            console.log("SignalR Connection Closed");
          });
        }
      } catch (error) {
        console.error("Error parsing user login:", error);
      }
    }

    // Polling interval changed from 2 minutes (120000ms) to 15 minutes (900000ms)
    const interval = setInterval(() => {
      fetchPendingDocuments();
      fetchNotifications();
    }, 900000);

    // Cleanup on unmount
    return () => {
      isMounted = false;
      clearInterval(interval);
      if (blinkIntervalRef.current) {
        clearInterval(blinkIntervalRef.current);
        blinkIntervalRef.current = null;
      }
      if (connection) {
        connection.stop().catch((err) => {
          console.error("Error stopping SignalR connection:", err);
        });
      }
    };
  }, []);

  useEffect(() => {
    // Calculate unread from displayed notifications
    const unreadNotifications = notifications.filter((n) => !n.read);
    const totalUnreadCount = pendingCount + unreadNotifications.length;
    updateDocumentTitle(totalUnreadCount);
  }, [pendingCount, notifications]);

  // Calculate unread from displayed notifications
  const unreadNotifications = notifications.filter((n) => !n.read);
  const totalUnreadCount = pendingCount + unreadNotifications.length;

  return (
    <Menu
      shadow="xl"
      width={420}
      position="bottom-end"
      opened={menuOpened}
      onChange={setMenuOpened}
      transitionProps={{ transition: "pop-top-right", duration: 200 }}
      radius="lg"
    >
      <Menu.Target>
        <ActionIcon
          variant="transparent"
          color="gray.7"
          size="lg"
          onClick={() => setMenuOpened(!menuOpened)}
          style={{
            position: "relative",
          }}
        >
          <IconBell size={24} stroke={1.5} color="#4a90d9" />
          {totalUnreadCount > 0 && (
            <Badge
              size="sm"
              color="red"
              radius="xl"
              style={{
                position: "absolute",
                top: -2,
                right: -2,
                minWidth: 18,
                height: 18,
                padding: "0 4px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 10,
                fontWeight: 700,
              }}
            >
              {totalUnreadCount > 99 ? "99+" : totalUnreadCount}
            </Badge>
          )}
        </ActionIcon>
      </Menu.Target>

      <Menu.Dropdown
        p={0}
        style={{
          border: "none",
          boxShadow: "0 10px 40px rgba(0,0,0,0.12)",
        }}
      >
        {/* Header - Facebook style */}
        <Box
          p="md"
          style={{
            background: "#ffffff",
            borderBottom: "1px solid #e4e6eb",
          }}
        >
          <Flex justify="space-between" align="center">
            <Text fw={700} size="xl" c="dark">
              Thông báo
            </Text>
            <ActionIcon variant="subtle" color="gray" size="lg" radius="xl">
              <IconDots size={20} />
            </ActionIcon>
          </Flex>
        </Box>

        <Tabs defaultValue="comments" variant="pills" styles={{
          tab: {
            fontWeight: 600,
            fontSize: '15px',
            padding: '12px 20px',
            borderRadius: '8px',
            border: '1px solid #e4e6eb',
            backgroundColor: '#f0f2f5',
            color: '#65676b',
            transition: 'all 0.2s ease',
            '&[data-active]': {
              backgroundColor: '#3399FF',
              color: '#ffffff',
              border: '1px solid #3399FF',
              boxShadow: '0 2px 4px rgba(51, 153, 255, 0.2)',
            },
            '&:not([data-active])': {
              backgroundColor: '#f0f2f5',
              color: '#65676b',
              border: '1px solid #e4e6eb',
              '&:hover': {
                backgroundColor: '#e4e6eb',
              }
            }
          },
          panel: { paddingTop: 0 }
        }}>
          <Tabs.List style={{ borderBottom: '1px solid #e4e6eb', padding: '12px 16px', gap: '12px', backgroundColor: '#ffffff' }}>
            <Tabs.Tab
              value="comments"
              rightSection={
                unreadNotifications.length > 0 && (
                  <Badge
                    size="sm"
                    circle
                    style={{
                      minWidth: 22,
                      height: 22,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 11,
                      fontWeight: 700,
                      marginLeft: 8,
                      backgroundColor: '#FF9900',
                      color: '#ffffff',
                      border: 'none',
                    }}
                  >
                    {unreadNotifications.length > 99 ? "99+" : unreadNotifications.length}
                  </Badge>
                )
              }
            >
              Bình luận
            </Tabs.Tab>
            <Tabs.Tab
              value="pending"
              rightSection={
                pendingCount > 0 && (
                  <Badge
                    size="sm"
                    circle
                    style={{
                      minWidth: 22,
                      height: 22,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 11,
                      fontWeight: 700,
                      marginLeft: 8,
                      backgroundColor: '#663399',
                      color: '#ffffff',
                      border: 'none',
                    }}
                  >
                    {pendingCount > 99 ? "99+" : pendingCount}
                  </Badge>
                )
              }
            >
              Hồ sơ chờ ký
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="comments">
            <ScrollArea h={500} scrollbarSize={6}>
              {notifications.length > 0 ? (
                <Box>
                  {notifications.map((notification) => (
                    <Box
                      key={notification.id}
                      p="md"
                      style={{
                        cursor: "pointer",
                        backgroundColor: "#ffffff",
                        borderBottom: "1px solid #e4e6eb",
                        transition: "background-color 0.2s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "#f2f3f5";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "#ffffff";
                      }}
                      onClick={() => handleNotificationClick(notification)}
                    >
                      <Flex gap="md" align="flex-start">
                        {/* Avatar */}
                        <Box style={{ position: 'relative', flexShrink: 0 }}>
                          {notification.creatorAvatar ? (
                            <Avatar
                              src={proxyAvatar(notification.creatorAvatar, 48)}
                              size={48}
                              radius="xl"
                              style={{
                                borderRadius: '50%',
                                overflow: 'hidden',
                              }}
                            >
                              {notification.creatorName?.[0] || "U"}
                            </Avatar>
                          ) : notification.creatorName ? (
                            <Box style={{ width: 48, height: 48, borderRadius: '50%', overflow: 'hidden' }}>
                              <Avvvatars
                                size={48}
                                style="character"
                                value={notification.creatorName}
                              />
                            </Box>
                          ) : (
                            <Box style={{ width: 48, height: 48, borderRadius: '50%', backgroundColor: '#e4e6eb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <IconMessage size={24} color="#65676b" />
                            </Box>
                          )}
                          {/* Unread indicator dot */}
                          {!notification.read && (
                            <Box
                              style={{
                                position: 'absolute',
                                top: 0,
                                right: 0,
                                width: 12,
                                height: 12,
                                borderRadius: '50%',
                                backgroundColor: '#1877f2',
                                border: '2px solid #ffffff',
                              }}
                            />
                          )}
                        </Box>
                        <Box style={{ flex: 1, minWidth: 0 }}>
                          <Text size="sm" fw={notification.read ? 400 : 500} c="dark" lineClamp={3}>
                            {notification.creatorName ? (
                              <>
                                <Text span fw={600} c="dark">{notification.creatorName}</Text>
                                <Text span c="dark.7"> {notification.content}</Text>
                              </>
                            ) : (
                              notification.content
                            )}
                            {notification.documentName && (
                              <Text size="xs" c="dimmed" mt={4} style={{ display: 'block' }}>
                                HS: {notification.documentName}
                              </Text>
                            )}
                          </Text>
                          <Text size="xs" c="dimmed" mt={4}>
                            {formatDateTransfer(notification.createDate)}
                          </Text>
                        </Box>
                      </Flex>
                    </Box>
                  ))}

                  {/* Nút Xem thông báo trước đó */}
                  {notificationPage.hasMore && notifications.length > 0 && (
                    <Box p="md" ta="center" style={{ borderTop: "1px solid #e4e6eb" }}>
                      <Button
                        variant="subtle"
                        size="md"
                        radius="md"
                        loading={isLoadingMore}
                        onClick={loadMoreNotifications}
                        fullWidth
                        style={{
                          fontWeight: 500,
                          color: '#65676b',
                          backgroundColor: '#f0f2f5',
                        }}
                      >
                        {isLoadingMore ? "Đang tải..." : "Xem thông báo trước đó"}
                      </Button>
                    </Box>
                  )}
                </Box>
              ) : (
                <Box p="xl" ta="center" mt="md">
                  <ThemeIcon size={60} variant="light" color="gray" radius="xl" mx="auto" mb="md">
                    <IconMessage size={30} />
                  </ThemeIcon>
                  <Text c="dimmed" size="sm">Không có thông báo nào</Text>
                </Box>
              )}
            </ScrollArea>
          </Tabs.Panel>


          <Tabs.Panel value="pending">
            <ScrollArea h={500} scrollbarSize={6}>
              {pendingDocuments.length > 0 ? (
                <Box>
                  {pendingDocuments.map((doc) => {
                    const signTypeInfo = getSignTypeInfo(Number(doc.typesigncustomer));
                    return (
                      <Box
                        key={doc.documentId}
                        p="md"
                        style={{
                          cursor: "pointer",
                          backgroundColor: "#ffffff",
                          borderBottom: "1px solid #e4e6eb",
                          transition: "background-color 0.2s ease",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = "#f2f3f5";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = "#ffffff";
                        }}
                        onClick={() => handleDocumentClick(doc)}
                      >
                        <Flex gap="md" align="flex-start">
                          {/* Icon */}
                          <Box style={{ flexShrink: 0 }}>
                            <ThemeIcon
                              size={48}
                              variant="light"
                              color="violet"
                              radius="xl"
                            >
                              <IconFileText size={24} />
                            </ThemeIcon>
                          </Box>
                          <Box style={{ flex: 1, minWidth: 0 }}>
                            <Group gap="xs" mb={6}>
                              <Badge size="sm" variant="light" color="indigo" radius="sm" fw={600}>
                                {doc.codeDocument}
                              </Badge>
                              <Badge size="xs" variant="dot" color={signTypeInfo.color}>
                                {signTypeInfo.label}
                              </Badge>
                            </Group>
                            <Text size="sm" fw={500} c="dark" lineClamp={2} mb={4}>
                              {doc.nameDocument || `Hồ sơ #${doc.documentId}`}
                            </Text>
                            <Group gap="xs" mb={4}>
                              <Text size="xs" c="dimmed">{doc.nameStep}</Text>
                              <Text size="xs" c="dimmed">•</Text>
                              <Flex align="center" gap={4}>
                                <IconClock size={12} color="#65676b" />
                                <Text size="xs" c="dimmed">
                                  {doc.createDate ? new Date(doc.createDate).toLocaleDateString("vi-VN") : "N/A"}
                                </Text>
                              </Flex>
                            </Group>
                          </Box>
                        </Flex>
                      </Box>
                    );
                  })}
                </Box>
              ) : (
                <Box p="xl" ta="center" mt="md">
                  <ThemeIcon size={60} variant="light" color="gray" radius="xl" mx="auto" mb="md">
                    <IconCheck size={30} />
                  </ThemeIcon>
                  <Text c="dimmed" size="sm">Không có hồ sơ chờ ký</Text>
                </Box>
              )}
            </ScrollArea>
            {pendingDocuments.length > 0 && (
              <Box p="md" ta="center" style={{ borderTop: "1px solid #e4e6eb" }}>
                <Button
                  variant="subtle"
                  size="md"
                  radius="md"
                  fullWidth
                  rightSection={<IconChevronRight size={16} />}
                  onClick={handleViewAll}
                  style={{
                    fontWeight: 500,
                    color: '#65676b',
                    backgroundColor: '#f0f2f5',
                  }}
                >
                  Xem tất cả hồ sơ
                </Button>
              </Box>
            )}
          </Tabs.Panel>
        </Tabs>
      </Menu.Dropdown>
    </Menu>
  );
};
